import React, { useState } from "react"

import { RequiredMarker } from "@/metrics/registry/configuration"
import { Button, Card, CardContent } from "@coval/ui"
import { ChevronDown, ChevronUp, Copy, HelpCircle } from "lucide-react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"

interface ExpectedBodyInputProps {
  value: string
  onChange: (value: string) => void
  isRequired?: boolean
}

const TEMPLATE_VARIABLES = [
  {
    name: "{{expected_output.balance}}",
    description: "From test_case.expected_output_json",
    example: '{"balance": "{{expected_output.balance}}"}',
  },
  {
    name: "{{agent.api_key}}",
    description: "From agent.attributes",
    example: '{"api_key": "{{agent.api_key}}"}',
  },
  {
    name: "{{test_case.field}}",
    description: "Any field from test_case",
    example: '{"card_last4": "{{test_case.card_last4}}"}',
  },
]

const EXAMPLES = [
  {
    title: "Simple JSON object",
    value: '{"status": "success", "balance": 100.50}',
  },
  {
    title: "With template variable",
    value: '{"balance": "{{expected_output.balance}}"}',
  },
  {
    title: "Nested object",
    value: '{"data": {"user": {"balance": "{{expected_output.balance}}"}}}',
  },
  {
    title: "String value",
    value: '"success"',
  },
]

export const ExpectedBodyInput: React.FC<ExpectedBodyInputProps> = ({
  value,
  onChange,
  isRequired = true,
}) => {
  const [showHelp, setShowHelp] = useState(false)
  const [showTemplateVars, setShowTemplateVars] = useState(false)
  const [showExamples, setShowExamples] = useState(false)

  const handleExampleClick = (exampleValue: string) => {
    onChange(exampleValue)
  }

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-1 flex-1">
          <label className="text-sm font-medium">
            Expected Body
            {isRequired && <RequiredMarker />}
          </label>
          <JsonCodeEditor
            ariaLabel="Expected Body"
            ariaRequired={isRequired}
            placeholder={
              '{\n  "status": "success"\n}\n\nor use {{expected_output.balance}}'
            }
            value={value}
            rows={3}
            onChange={onChange}
            // The value may be a bare string or a lone template variable, so a
            // plain JSON parse is the wrong bar for this field.
            lint={false}
          />
          <p className="text-xs text-coval-text-muted">
            The expected response body to match against. Can be a JSON object,
            string, or use template variables.
          </p>
        </div>
        <Button
          icon
          variant="ghost"
          onClick={() => setShowHelp(!showHelp)}
          className="self-start mt-6"
          tooltip="Toggle help"
        >
          <HelpCircle className="w-5 h-5 text-coval-text-muted" />
        </Button>
      </div>

      {showHelp && (
        <Card className="mt-2 border border-coval-border">
          <CardContent className="flex flex-col gap-4 pt-6">
            <div>
              <h4 className="font-semibold text-sm mb-2">
                What is Expected Body?
              </h4>
              <p className="text-sm text-coval-text-muted mb-3">
                This is the value you expect to find in the API response. The
                metric will compare this against the actual response (or a
                specific field if you use Match Path).
              </p>
              <p className="text-sm text-coval-text-muted">
                <strong>Tip:</strong> If you specify a Match Path, the Expected
                Body should match the value at that path. If no Match Path is
                specified, the entire response body will be compared.
              </p>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowTemplateVars(!showTemplateVars)}
                className="flex items-center gap-2 text-sm font-medium text-coval-info-text hover:underline transition-colors"
              >
                {showTemplateVars ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                <span>Template Variables</span>
              </button>
              {showTemplateVars && (
                <div className="mt-2 space-y-2">
                  {TEMPLATE_VARIABLES.map((variable) => (
                    <div
                      key={variable.name}
                      className="p-2 bg-surface-1 rounded-lg text-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <code className="text-coval-info-text font-mono text-xs">
                            {variable.name}
                          </code>
                          <p className="text-coval-text-muted text-xs mt-1">
                            {variable.description}
                          </p>
                        </div>
                        <Button
                          icon
                          variant="ghost"
                          onClick={() => copyToClipboard(variable.example)}
                          className="shrink-0"
                          tooltip="Copy expected body"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowExamples(!showExamples)}
                className="flex items-center gap-2 text-sm font-medium text-coval-info-text hover:underline transition-colors"
              >
                {showExamples ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                <span>Example Values</span>
              </button>
              {showExamples && (
                <div className="mt-2 space-y-2">
                  {EXAMPLES.map((example, idx) => (
                    <div
                      key={idx}
                      className="p-2 bg-surface-1 rounded-lg text-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="font-medium text-xs mb-1">
                            {example.title}
                          </p>
                          <code className="text-coval-info-text font-mono text-xs break-all">
                            {example.value}
                          </code>
                        </div>
                        <Button
                          icon
                          variant="ghost"
                          onClick={() => handleExampleClick(example.value)}
                          className="shrink-0"
                          tooltip={`Use ${example.title}`}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
